import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import { getLastSeenProductIdsStorage } from '@jsmdg/browser-storage';
import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { trackCategoryTree } from '@jsmdg/tracking';
import {
    Breakpoint,
    DiscountBadge,
    DiscountBadgeShape,
    NotificationSnackbar,
    SnackbarPosition,
    useBreakpoint,
} from '@jsmdg/yoshi';
import { getBookableLocationsCount, isExperience } from '../../../shared/product-safeguards';
import { ProductDetailScrollProvider } from '../../contexts';
import { getPurchaseType } from '../../helper/getPurchaseType';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { PriceProvider } from '../../providers';
import { type ProductDetailContext } from '../../types/types';
import { AttributesContainer } from '../AttributesContainer';
import { BuyboxContainer } from '../BuyboxContainer';
import { trackEECProductDetailViewed } from '../BuyboxContainer/buyboxContainer.tracking';
import { MediaCarouselContainer } from '../ImageCarousel/MediaCarouselContainer';
import { ImageGallery } from '../ImageGallery';
import { ProductInfo } from '../ProductInfo';
import styles from './ProductDetail.module.scss';

const messages = defineMessages({
    a11YCloseErrorMessage: { defaultMessage: 'Schließen' },
});

const ProductDetail = (): JSX.Element => {
    const product = useProduct();
    const bookableLocationsCount = getBookableLocationsCount(product);
    const { price } = usePrice();

    const isUpToSMBreakpoint = useBreakpoint(Breakpoint.SM);
    const upToXSBreakpoint = useBreakpoint(Breakpoint.XS);
    const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

    const { attributes, discountBadge, mediaContent, productId, title, trackingPath } = product;
    const { isBoxProductWithQrCodeLink, isMydays, isTopRecommendationEnabled } =
        useFragmentContext<ProductDetailContext>();

    const intl = useIntl();

    useEffect(() => {
        getLastSeenProductIdsStorage().add(productId);
    }, [productId]);

    useEffect(() => {
        trackCategoryTree(trackingPath);
    }, [trackingPath]);

    useEffect(() => {
        const locations = isExperience(product) ? product.locations : [];
        const sellOption = isExperience(product) ? product.sellOption : undefined;

        trackEECProductDetailViewed(
            product,
            price,
            locations,
            getPurchaseType(sellOption),
            isExperience(product) ? product.minParticipants : undefined,
            isExperience(product) ? product.maxParticipants : undefined,
            bookableLocationsCount,
        );
    }, [bookableLocationsCount, price, product]);

    return (
        <PriceProvider>
            <ProductDetailScrollProvider>
                <div
                    className={classNames(styles.productDetail, 'px-2x p-sm-0')}
                    data-root="algolia-search"
                >
                    <div
                        className={classNames(
                            styles.withOverlay,
                            isTopRecommendationEnabled && 'd-none',
                        )}
                    />
                    <NotificationSnackbar
                        className={upToXSBreakpoint ? 'container' : 'px-3x'}
                        position={SnackbarPosition.Bottom}
                        a11yCloseButtonText={intl.formatMessage(messages.a11YCloseErrorMessage)}
                    />
                    <div
                        className={classNames(
                            'position-relative overflow-hidden',
                            styles.imageContainerWrapper,
                        )}
                    >
                        {!isBoxProductWithQrCodeLink && discountBadge && isUpToSMBreakpoint && (
                            <DiscountBadge
                                percentage={discountBadge?.percentage}
                                text={discountBadge.text}
                                shape={
                                    isMydays
                                        ? DiscountBadgeShape.Pill
                                        : DiscountBadgeShape.Rectangle
                                }
                                withHint={isMydays}
                                primaryColor={discountBadge.primaryColor}
                                secondaryColor={discountBadge.secondaryColor}
                                textColor={discountBadge.textColor}
                                isBig
                                className={classNames(
                                    'position-absolute',
                                    styles.discountBadgeWrapper,
                                )}
                            />
                        )}
                        <div className={classNames('d-block', !isVideoFullscreen && 'd-sm-none')}>
                            <MediaCarouselContainer
                                onVideoFullscreenChange={setIsVideoFullscreen}
                            />
                        </div>
                        <div className="d-none d-sm-block">
                            <ImageGallery title={title} mediaContent={mediaContent} />
                        </div>
                    </div>
                    <article className={classNames(styles.buyboxWrapper, 'px-0 px-sm-6x')}>
                        <BuyboxContainer />
                    </article>
                    <div className={classNames(styles.infoWrapper, 'mt-sm-2x mt-3x px-sm-0 px-0')}>
                        <AttributesContainer attributes={attributes} />
                        <ProductInfo />
                    </div>
                </div>
            </ProductDetailScrollProvider>
        </PriceProvider>
    );
};

export { ProductDetail };
